import { useMemo } from "react";
import { ArrowRight, Clock3, FileCheck2, Landmark, ScrollText, UserX, Lock } from "lucide-react";
import { StepShell, Eyebrow, Title, BottomBar, PrimaryButton, GhostButton } from "./ui";
import { TrustStrip } from "../site/TrustStrip";
import { IndiaFlag } from "../site/IndiaFlag";
import { RouteCompare } from "./RouteCompare";
import { detectCountry, countryName } from "../../lib/journey";

const DIASPORA = [["AE", "UAE"], ["US", "USA"], ["GB", "UK"], ["SA", "Saudi Arabia"], ["QA", "Qatar"], ["CA", "Canada"],
  ["SG", "Singapore"], ["AU", "Australia"], ["OM", "Oman"], ["KW", "Kuwait"]];

const EASE = [
  { icon: Clock3, text: "About 2 minutes" },
  { icon: UserX, text: "No sign-up" },
  { icon: Lock, text: "Contact details only at the end" },
];

export const Opening = ({ onYes, onExplore }) => {
  const iso = useMemo(detectCountry, []);
  const abroad = iso !== "IN";
  return (
    <StepShell stepKey="opening" testId="screen-opening">
      <div className="flex flex-1 flex-col justify-center pb-8">
        <div className="mb-6 flex items-center gap-3" data-testid="opening-namaste">
          <IndiaFlag className="h-9 w-[54px]" testId="opening-india-flag" />
          <div className="leading-tight">
            <p className="font-display text-2xl text-[#1f2328]" lang="hi">नमस्ते</p>
            <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-[var(--saffron-deep)]">Welcome home, wherever you are</p>
          </div>
        </div>
        <Title>Living abroad. Thinking about owning a property in India?</Title>
        <p className="mt-4 text-[15px] font-medium text-[#138808]" data-testid="opening-personal-line">
          {abroad ? `Made for Indians living in ${countryName(iso)} and around the world.` : "Made for Indians living abroad — and their families back home."}
        </p>
        <p className="mt-3 max-w-md text-base text-neutral-600">
          A calm, guided way to find the right home or investment back home — from an India-based team that understands NRI needs. No endless calls, no wasted site visits.
        </p>
        <div className="mt-6" data-testid="opening-diaspora">
          <p className="mb-2 text-[11px] font-semibold uppercase tracking-[0.14em] text-neutral-500">For Indians in</p>
          <div className="flex flex-wrap gap-1.5">
            {DIASPORA.map(([c, label]) => (
              <span key={c} className={`rounded-full border px-2.5 py-1 text-xs ${c === iso ? "border-[#138808] bg-[#138808]/10 font-semibold text-[#0f6d06]" : "border-neutral-200 bg-white text-neutral-600"}`}>
                {label}
              </span>
            ))}
            <span className="rounded-full border border-neutral-200 bg-white px-2.5 py-1 text-xs text-neutral-600">& everywhere else</span>
          </div>
        </div>
        <ul className="mt-6 flex flex-wrap gap-x-5 gap-y-2 text-[13px] text-neutral-700" data-testid="opening-ease">
          {EASE.map(({ icon: Icon, text }) => (
            <li key={text} className="flex items-center gap-1.5"><Icon className="h-4 w-4 text-[var(--saffron-deep)]" strokeWidth={1.8} />{text}</li>
          ))}
        </ul>
        <div className="mt-6 border-t border-neutral-200/80 pt-5"><TrustStrip /></div>
      </div>
      <BottomBar>
        <div className="space-y-3">
          <PrimaryButton onClick={onYes} data-testid="opening-yes-button">Yes, I am <ArrowRight className="h-5 w-5" /></PrimaryButton>
          <GhostButton onClick={onExplore} data-testid="opening-explore-button">Just exploring</GhostButton>
        </div>
      </BottomBar>
    </StepShell>
  );
};

const WHY = [
  { icon: Landmark, title: "FEMA-aware purchase guidance", text: "General guidance on how NRIs typically buy and pay for property in India." },
  { icon: ScrollText, title: "Power of Attorney support", text: "Help understanding when a PoA may be useful and how families usually arrange it." },
  { icon: FileCheck2, title: "Remote documentation help", text: "Coordination support so more of the paperwork can happen from abroad." },
];

export const Intro = ({ onNext, onBack }) => (
  <StepShell stepKey="intro" onBack={onBack} testId="screen-intro">
    <div className="flex-1">
      <Eyebrow>How it works</Eyebrow>
      <Title>Your India Property Search Starts Before Your India Visit.</Title>
      <p className="mt-5 text-base text-neutral-600">
        Share what you're looking for from wherever you live. We shortlist suitable projects first, so your time in India is spent with family — not in traffic between sites.
      </p>
      <div className="mt-8 rounded-3xl border border-neutral-200 bg-[var(--ivory)] p-5" data-testid="why-nris-trust-us">
        <p className="font-display text-lg text-[#1f2328]">Why NRIs trust us</p>
        <ul className="mt-4 space-y-4">
          {WHY.map(({ icon: Icon, title, text }) => (
            <li key={title} className="flex gap-3">
              <Icon className="mt-0.5 h-5 w-5 shrink-0 text-[var(--saffron-deep)]" strokeWidth={1.7} />
              <div>
                <p className="text-sm font-semibold text-[#1f2328]">{title}</p>
                <p className="text-[13px] text-neutral-600">{text}</p>
              </div>
            </li>
          ))}
        </ul>
        <p className="mt-4 text-[11px] text-neutral-500">General information only, not legal or tax advice.</p>
      </div>
      <div className="mt-6"><TrustStrip /></div>
    </div>
    <BottomBar>
      <PrimaryButton onClick={onNext} data-testid="intro-tell-me-how-button">Tell Me How <ArrowRight className="h-5 w-5" /></PrimaryButton>
    </BottomBar>
  </StepShell>
);

export const Compare = ({ onNext, onBack }) => (
  <StepShell stepKey="compare" onBack={onBack} testId="screen-compare">
    <div className="flex-1">
      <Title>Pehle Match. Phir Site Visit.</Title>
      <p className="mt-4 text-base text-neutral-600">
        We first match you with suitable projects, then help you plan site visits during your India trip, only if needed.
      </p>
      <p className="mt-2 text-xs uppercase tracking-[0.14em] text-neutral-500" data-testid="compare-powered-caption">
        MatchMaking powered by ConectR MatchMaking Technology
      </p>
      <RouteCompare />
    </div>
    <BottomBar>
      <PrimaryButton onClick={onNext} data-testid="compare-find-matches-button">Find My Property Matches <ArrowRight className="h-5 w-5" /></PrimaryButton>
    </BottomBar>
  </StepShell>
);
